import { useQuery } from "@tanstack/react-query";
import { fetchMissingTenors, fetchTenorFormContext } from "api/pricerApi";
import {
  fetchCustomerContracts,
  fetchCustomerProducts,
  fetchCustomerStyles,
} from "api/marketAnalysisApi";
import { parseAddTenorCatalogId } from "constants/addTenorTable";
import {
  getMissingTenorsAvailabilityHint,
  mapMissingTenorsResponse,
} from "helpers/Pricer/optionRowTenorCreation";

function mapFormContextSelectOptions(items) {
  const list = Array.isArray(items) ? items : [];
  return list
    .map((item) => ({
      value: item?.id,
      label: String(item?.title ?? "").trim(),
    }))
    .filter((o) => o.value != null && o.label);
}

function selectFormContextSlice(response) {
  const data = response?.data?.data ?? {};
  return {
    contractOptions: mapFormContextSelectOptions(data.contracts),
    styleOptions: mapFormContextSelectOptions(data.styles),
    periodOptions: mapFormContextSelectOptions(data.periods),
    defaults: data.defaults ?? {},
  };
}

function mapSelectOptions(response) {
  const rows = response?.data?.data ?? [];
  const list = Array.isArray(rows) ? rows : [];
  return list
    .map((item) => ({
      value: item?.id ?? item?.value,
      label: item?.title ?? item?.name ?? item?.label ?? "",
    }))
    .filter((o) => o.value != null && o.label);
}

function mapStyleOptions(response) {
  const rows = response?.data?.data ?? [];
  const list = Array.isArray(rows) ? rows : [];
  return list
    .map((item) => ({
      value: item?.style_id ?? item?.id ?? item?.value,
      label: item?.title ?? item?.name ?? item?.label ?? "",
    }))
    .filter((o) => o.value != null && o.label);
}

function styleMatchesPcs(styleItem, pcsId) {
  const pcs = Number(pcsId);
  if (!Number.isFinite(pcs)) return false;
  return (
    Number(styleItem?.product_contract_style_id) === pcs ||
    Number(styleItem?.pcs_id) === pcs
  );
}

/** Walk customer catalog to map a PCS shortcut id to product / contract / style ids. */
export async function resolveAddTenorCatalogFromPcs(pcsId) {
  const targetPcs = parseAddTenorCatalogId(pcsId);
  if (targetPcs == null) return null;

  const productsRes = await fetchCustomerProducts();
  const products = productsRes?.data?.data ?? [];
  if (!Array.isArray(products)) return null;

  for (const product of products) {
    const product_id = product?.id;
    if (product_id == null) continue;

    const contractsRes = await fetchCustomerContracts({ product_id });
    const contracts = contractsRes?.data?.data ?? [];
    if (!Array.isArray(contracts)) continue;

    for (const contract of contracts) {
      const contract_id = contract?.id;
      if (contract_id == null) continue;

      const stylesRes = await fetchCustomerStyles({ product_id, contract_id });
      const styles = stylesRes?.data?.data ?? [];
      if (!Array.isArray(styles)) continue;

      const style = styles.find((item) => styleMatchesPcs(item, targetPcs));
      if (style) {
        const resolved = {
          product_id: parseAddTenorCatalogId(product_id),
          contract_id: parseAddTenorCatalogId(contract_id),
          style_id: parseAddTenorCatalogId(style?.style_id ?? style?.id),
          product_label: product?.title ?? product?.name ?? "",
          contract_label: contract?.title ?? contract?.name ?? "",
          style_display: style?.title ?? style?.name ?? "",
        };
        if (
          resolved.product_id != null &&
          resolved.contract_id != null &&
          resolved.style_id != null
        ) {
          return resolved;
        }
      }
    }
  }

  return null;
}

export function useResolveAddTenorCatalogFromPcs(pcsId, enabled) {
  return useQuery({
    queryKey: ["add-tenor-resolve-pcs", pcsId],
    queryFn: () => resolveAddTenorCatalogFromPcs(pcsId),
    enabled: Boolean(enabled && parseAddTenorCatalogId(pcsId) != null),
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useAddTenorCatalog() {
  const productsQuery = useQuery({
    queryKey: ["add-tenor-products"],
    queryFn: () => fetchCustomerProducts(),
    refetchOnWindowFocus: false,
  });

  const productOptions = mapSelectOptions(productsQuery.data);

  return {
    productOptions,
    productsLoading: productsQuery.isLoading,
  };
}

export function useAddTenorContracts(productId) {
  const product_id = parseAddTenorCatalogId(productId);
  return useQuery({
    queryKey: ["add-tenor-form-context", product_id],
    queryFn: () => fetchTenorFormContext({ product_id }),
    enabled: product_id != null,
    refetchOnWindowFocus: false,
    select: (res) => {
      const slice = selectFormContextSlice(res);
      return {
        options: slice.contractOptions,
        defaults: slice.defaults,
      };
    },
  });
}

export function useAddTenorStyles(productId, contractId) {
  const product_id = parseAddTenorCatalogId(productId);
  const contract_id = parseAddTenorCatalogId(contractId);
  return useQuery({
    queryKey: ["add-tenor-form-context", product_id, contract_id],
    queryFn: () =>
      fetchTenorFormContext({ product_id, contract_id }),
    enabled: product_id != null && contract_id != null,
    refetchOnWindowFocus: false,
    select: (res) => {
      const slice = selectFormContextSlice(res);
      return {
        options: slice.styleOptions,
        defaults: slice.defaults,
      };
    },
  });
}

export function useAddTenorPeriodTypes(catalogParams) {
  const product_id = parseAddTenorCatalogId(catalogParams?.product_id);
  const contract_id = parseAddTenorCatalogId(catalogParams?.contract_id);
  const style_id = parseAddTenorCatalogId(catalogParams?.style_id);
  const enabled =
    product_id != null && contract_id != null && style_id != null;

  return useQuery({
    queryKey: ["add-tenor-form-context", product_id, contract_id, style_id],
    queryFn: () =>
      fetchTenorFormContext({ product_id, contract_id, style_id }),
    enabled,
    refetchOnWindowFocus: false,
    select: (res) => {
      const slice = selectFormContextSlice(res);
      return {
        options: slice.periodOptions,
        defaults: slice.defaults,
      };
    },
  });
}

/**
 * Tenor labels for create or update — from GET /option-rows/tenors/missing.
 */
export function useAddTenorTenorOptions(catalogParams, periodTypeId, periodLabel) {
  const period_id = parseAddTenorCatalogId(periodTypeId);
  const queryEnabled =
    catalogParams?.product_id != null &&
    catalogParams?.contract_id != null &&
    catalogParams?.style_id != null &&
    period_id != null;

  const missingQuery = useQuery({
    queryKey: [
      "add-tenor-missing",
      catalogParams?.product_id,
      catalogParams?.contract_id,
      catalogParams?.style_id,
      period_id,
    ],
    queryFn: () =>
      fetchMissingTenors({
        product_id: catalogParams.product_id,
        contract_id: catalogParams.contract_id,
        style_id: catalogParams.style_id,
        period_id,
      }),
    enabled: queryEnabled,
    refetchOnWindowFocus: false,
  });

  const mapped = mapMissingTenorsResponse(missingQuery.data, periodLabel);

  return {
    options: mapped.options,
    ulOptions: mapped.ulOptions,
    lastExistingTenor: mapped.lastExistingTenor,
    defaultSublegs: mapped.defaultSublegs,
    enableStartDate: mapped.enableStartDate,
    expiryCalendarLinks: mapped.links,
    availabilityHint: getMissingTenorsAvailabilityHint(mapped, periodLabel),
    isLoading: queryEnabled && missingQuery.isFetching,
  };
}
